import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import {
  mapGeneratedArtifacts,
  mapUploadedArtifactFiles,
  type ArtifactSelectionArtifact as ArtifactItem,
  type ArtifactSelectionFile as FileItem,
  type ArtifactSelectionItem as UnifiedItem,
} from "@/lib/artifactSelection";
import { AuthImg } from "./ArtifactCanvas";

type ViewMode = "preview" | "list";
type TabOrigin = "all" | "mine" | "agent";

const PAGE_SIZE = 20;

const STYLE_CLASSES: Record<string, { bg: string; border: string; text: string; hover: string }> = {
  emerald: {
    bg: "bg-seexr-50 dark:bg-seexr-950/30",
    border: "border-seexr-100 dark:border-seexr-900/50",
    text: "text-seexr-600 dark:text-seexr-400",
    hover: "hover:border-seexr-500/50",
  },
  blue: {
    bg: "bg-seexr-50 dark:bg-seexr-950/30",
    border: "border-seexr-100 dark:border-seexr-900/50",
    text: "text-seexr-600 dark:text-seexr-400",
    hover: "hover:border-seexr-500/50",
  },
  amber: {
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-100 dark:border-amber-900/50",
    text: "text-amber-600 dark:text-amber-400",
    hover: "hover:border-amber-500/50",
  },
  violet: {
    bg: "bg-violet-50 dark:bg-violet-950/30",
    border: "border-violet-100 dark:border-violet-900/50",
    text: "text-violet-600 dark:text-violet-400",
    hover: "hover:border-violet-500/50",
  },
  rose: {
    bg: "bg-rose-50 dark:bg-rose-950/30",
    border: "border-rose-100 dark:border-rose-900/50",
    text: "text-rose-600 dark:text-rose-400",
    hover: "hover:border-rose-500/50",
  },
  gray: {
    bg: "bg-gray-100 dark:bg-[#1f1f22]",
    border: "border-gray-200 dark:border-[#2a2a2e]",
    text: "text-gray-500 dark:text-gray-400",
    hover: "hover:border-seexr-primary/40",
  },
};

const iconDoc = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const iconShield = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
);
const iconSearch = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
);
const iconGavel = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><rect x="3" y="16" width="6" height="4" rx="1"/><rect x="15" y="16" width="6" height="4" rx="1"/><path d="M12 7l-6 9"/><path d="M12 7l6 9"/></svg>
);
const iconImage = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
);
const iconPdf = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><text x="7" y="18" fontSize="6" fontWeight="900" fill="currentColor" stroke="none">PDF</text></svg>
);

function getArtifactVisual(type: string) {
  if (type === "risk_assessment") return { color: "emerald", icon: iconShield, label: "risk assessment" };
  if (type === "tbm_scenario") return { color: "blue", icon: iconShield, label: "TBM scenario" };
  if (type === "tbm_record") return { color: "blue", icon: iconDoc, label: "TBM record" };
  if (type === "cases" || type === "accident_search") return { color: "blue", icon: iconSearch, label: "cases" };
  if (type === "laws" || type === "legal_search") return { color: "amber", icon: iconGavel, label: "laws" };
  return { color: "gray", icon: iconDoc, label: (type || "document").replace("_", " ") };
}

function getFileVisual(mimeType: string) {
  if (mimeType?.startsWith("image/")) return { color: "violet", icon: iconImage, isImage: true };
  if (mimeType === "application/pdf") return { color: "rose", icon: iconPdf, isImage: false };
  return { color: "gray", icon: iconDoc, isImage: false };
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

function SelectionCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={`flex items-center justify-center w-5 h-5 rounded-md border-2 shrink-0 transition-colors ${
        checked
          ? "bg-seexr-solid border-seexr-primary text-white"
          : "bg-white dark:bg-[#0c0c0e] border-gray-300 dark:border-gray-600"
      }`}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      )}
    </span>
  );
}

function PreviewCard({
  item,
  onClick,
  selectMode,
  selected,
}: {
  item: UnifiedItem;
  onClick: (item: UnifiedItem) => void;
  selectMode: boolean;
  selected: boolean;
}) {
  const isFile = item.kind === "file";
  const fv = isFile ? getFileVisual((item as FileItem).mimeType) : null;
  const av = !isFile ? getArtifactVisual((item as ArtifactItem).type) : null;
  const cls = STYLE_CLASSES[(fv?.color || av?.color || "gray") as string];
  const typeLabel = isFile
    ? ((item as FileItem).mimeType?.split("/")[1]?.toUpperCase() || "FILE")
    : (av?.label || "");
  const selectedRing = selected && selectMode ? "border-seexr-primary ring-2 ring-seexr-primary/30" : "border-gray-200 dark:border-[#2a2a2e]";

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className={`relative flex flex-col p-4 rounded-2xl border bg-white dark:bg-[#161618] text-left transition-all ${selectedRing} ${selectMode ? "" : cls.hover} hover:shadow-sm`}
    >
      {selectMode && (
        <span className="absolute top-2 right-2">
          <SelectionCheckbox checked={selected} />
        </span>
      )}
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border overflow-hidden ${cls.bg} ${cls.text} ${cls.border} ${fv?.isImage ? "p-0" : "p-2"} mb-3`}>
        {fv?.isImage ? (
          <AuthImg fileId={(item as FileItem).fileId} className="w-full h-full object-cover" alt={item.title} />
        ) : (fv?.icon || av?.icon)}
      </div>
      <h3 className="font-bold text-[14px] text-gray-900 dark:text-gray-100 line-clamp-2 mb-1">{item.title}</h3>
      <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{typeLabel}</span>
    </button>
  );
}

function ListRow({
  item,
  onClick,
  selectMode,
  selected,
}: {
  item: UnifiedItem;
  onClick: (item: UnifiedItem) => void;
  selectMode: boolean;
  selected: boolean;
}) {
  const isFile = item.kind === "file";
  const fv = isFile ? getFileVisual((item as FileItem).mimeType) : null;
  const av = !isFile ? getArtifactVisual((item as ArtifactItem).type) : null;
  const cls = STYLE_CLASSES[(fv?.color || av?.color || "gray") as string];
  const typeLabel = isFile
    ? ((item as FileItem).mimeType?.split("/")[1]?.toUpperCase() || "FILE")
    : (av?.label || "");
  const bg = selected && selectMode
    ? "bg-seexr-solid/5 hover:bg-seexr-solid/10"
    : "hover:bg-gray-50 dark:hover:bg-gray-800/50";

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors text-left w-full group ${bg}`}
    >
      {selectMode && <SelectionCheckbox checked={selected} />}
      <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center border overflow-hidden ${cls.bg} ${cls.text} ${cls.border} ${fv?.isImage ? "p-0" : "p-2"}`}>
        {fv?.isImage ? (
          <AuthImg fileId={(item as FileItem).fileId} className="w-full h-full object-cover" alt={item.title} />
        ) : (fv?.icon || av?.icon)}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-[14px] font-bold text-gray-900 dark:text-gray-100 truncate">{item.title}</span>
        <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">{typeLabel}</span>
      </div>
      <span className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0 hidden sm:inline">{formatDate(item.created_at)}</span>
      {!selectMode && (
        <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-seexr-primary shrink-0">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </div>
      )}
    </button>
  );
}

function PreviewSkeletonCard() {
  return (
    <div className="flex flex-col p-4 rounded-2xl border border-gray-100 dark:border-[#2a2a2e] bg-white dark:bg-[#161618] animate-pulse">
      <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-[#1f1f22] mb-3"></div>
      <div className="w-3/4 h-4 bg-gray-100 dark:bg-[#1f1f22] rounded mb-2"></div>
      <div className="w-1/2 h-3 bg-gray-100 dark:bg-[#1f1f22] rounded"></div>
    </div>
  );
}

function ListSkeletonRow() {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl animate-pulse">
      <div className="w-10 h-10 shrink-0 rounded-xl bg-gray-100 dark:bg-[#1f1f22]"></div>
      <div className="flex flex-col min-w-0 flex-1 gap-1.5">
        <div className="w-2/3 h-3.5 bg-gray-100 dark:bg-[#1f1f22] rounded"></div>
        <div className="w-1/3 h-3 bg-gray-100 dark:bg-[#1f1f22] rounded"></div>
      </div>
      <div className="w-12 h-3 bg-gray-100 dark:bg-[#1f1f22] rounded hidden sm:block"></div>
    </div>
  );
}

export const ArtifactSelectorModal = ({ onClose, onSelect }: { onClose: () => void; onSelect: (artifact: UnifiedItem) => void }) => {
  const [artifacts, setArtifacts] = useState<ArtifactItem[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("preview");
  const [activeTab, setActiveTab] = useState<TabOrigin>("all");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Record<string, UnifiedItem>>({});

  const offsetRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadArtifacts = useCallback(async (reset: boolean) => {
    const reqId = ++requestIdRef.current;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    const startTime = Date.now();
    try {
      const currentOffset = reset ? 0 : offsetRef.current;
      const qs = new URLSearchParams();
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(currentOffset));
      if (debouncedSearch.trim()) qs.set("search", debouncedSearch.trim());
      if (activeTab !== "all") qs.set("tab", activeTab);

      const res = await fetchWithAuth(`${apiUrl}/api/artifacts?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();

      // Keep the skeleton visible briefly so the progressive loading is perceivable
      const elapsed = Date.now() - startTime;
      const minDelay = reset ? 250 : 400;
      if (elapsed < minDelay) await new Promise(r => setTimeout(r, minDelay - elapsed));

      if (requestIdRef.current !== reqId) return;

      const mapped = mapGeneratedArtifacts(data);

      if (reset) {
        setArtifacts(mapped);
      } else {
        setArtifacts(prev => {
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...mapped.filter(m => !seen.has(m.id))];
        });
      }
      setTotalCount(data.count || 0);
      offsetRef.current = currentOffset + PAGE_SIZE;
    } catch (err) {
      console.error("Failed to fetch artifacts", err);
    } finally {
      if (requestIdRef.current === reqId) {
        if (reset) setLoading(false);
        else setLoadingMore(false);
      }
    }
  }, [apiUrl, debouncedSearch, activeTab]);

  // Files endpoint has no pagination — load once; filter client-side by search/tab.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`${apiUrl}/api/files`, { credentials: "include" });
        if (!res.ok) return;
        if (cancelled) return;
        const mapped = mapUploadedArtifactFiles(await res.json());
        setFiles(mapped);
      } catch (err) {
        console.error("Failed to fetch files", err);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl]);

  // Reload artifacts when search or tab changes (also runs on mount)
  useEffect(() => {
    offsetRef.current = 0;
    loadArtifacts(true);
  }, [loadArtifacts]);

  // Combine + sort; apply tab filter to files (agent tab excludes files)
  const combined: UnifiedItem[] = (() => {
    const q = debouncedSearch.trim().toLowerCase();
    const includeFiles = activeTab === "all" || activeTab === "mine";
    const fileItems = includeFiles ? files : [];
    const filteredFiles = q ? fileItems.filter(f => f.title.toLowerCase().includes(q)) : fileItems;
    const merged: UnifiedItem[] = [...artifacts, ...filteredFiles];
    merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return merged;
  })();

  const hasMore = offsetRef.current < totalCount;

  // Infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;
    const target = observerTarget.current;
    const root = scrollRef.current;
    if (!target || !root) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingMore) {
          loadArtifacts(false);
        }
      },
      { root, threshold: 0.1, rootMargin: "120px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadArtifacts, viewMode]);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleItemClick = (item: UnifiedItem) => {
    if (selectMode) {
      setSelected(prev => {
        const next = { ...prev };
        if (next[item.id]) delete next[item.id];
        else next[item.id] = item;
        return next;
      });
      return;
    }
    onSelect(item);
  };

  const toggleSelectMode = () => {
    setSelectMode(prev => {
      if (prev) setSelected({});
      return !prev;
    });
  };

  const confirmSelection = () => {
    const items = Object.values(selected);
    if (items.length === 0) return;
    items.forEach(item => onSelect(item));
  };

  const selectedCount = Object.keys(selected).length;

  if (typeof document === "undefined") return null;

  const showInitialSkeleton = loading && combined.length === 0;
  const showEmpty = !loading && combined.length === 0;

  const tabBtnCls = (active: boolean) =>
    `px-3 py-1.5 text-[13px] font-bold rounded-full transition-colors whitespace-nowrap ${
      active
        ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900"
        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
    }`;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white dark:bg-[#161618] w-[90%] max-w-[640px] h-[80vh] max-h-[640px] rounded-[24px] shadow-2xl flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-800/50 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-[18px] font-bold text-gray-900 dark:text-gray-100">아티팩트 선택</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleSelectMode}
              aria-label="Multi-select"
              aria-pressed={selectMode}
              title={selectMode ? "선택 취소" : "선택 활성화"}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-bold transition-colors ${
                selectMode
                  ? "bg-seexr-solid text-white hover:bg-seexr-solid/90"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 11 12 14 22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              <span>{selectMode ? "취소" : "선택"}</span>
            </button>
            <div className="flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-1">
              <button
                type="button"
                onClick={() => setViewMode("preview")}
                aria-label="Preview view"
                aria-pressed={viewMode === "preview"}
                className={`flex size-7 items-center justify-center rounded-full transition-colors ${viewMode === "preview" ? "bg-white dark:bg-[#2a2a2e] text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                aria-label="List view"
                aria-pressed={viewMode === "list"}
                className={`flex size-7 items-center justify-center rounded-full transition-colors ${viewMode === "list" ? "bg-white dark:bg-[#2a2a2e] text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </button>
            </div>
            <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="shrink-0 px-4 pt-4 pb-3">
          <div className="relative">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="아티팩트 검색..."
              className="w-full h-[44px] pl-10 pr-4 rounded-xl bg-gray-100/50 hover:bg-gray-100 focus:bg-white dark:bg-gray-800/50 dark:hover:bg-gray-800 dark:focus:bg-[#111112] border-2 border-transparent focus:border-seexr-primary/30 text-gray-900 dark:text-gray-100 outline-none transition-all placeholder:text-gray-400 font-medium"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 px-4 pb-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 overflow-x-auto">
          <button type="button" onClick={() => setActiveTab("all")} className={tabBtnCls(activeTab === "all")}>전체</button>
          <button type="button" onClick={() => setActiveTab("mine")} className={tabBtnCls(activeTab === "mine")}>사용자 업로드</button>
          <button type="button" onClick={() => setActiveTab("agent")} className={tabBtnCls(activeTab === "agent")}>에이전트 생성</button>
        </div>

        {/* Content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar p-4">
          {showInitialSkeleton ? (
            viewMode === "preview" ? (
              <div className="grid grid-cols-2 gap-3">
                {[...Array(6)].map((_, i) => <PreviewSkeletonCard key={`init-p-${i}`} />)}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {[...Array(8)].map((_, i) => <ListSkeletonRow key={`init-l-${i}`} />)}
              </div>
            )
          ) : showEmpty ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <p className="font-semibold text-[14px]">검색된 아티팩트가 없습니다</p>
            </div>
          ) : viewMode === "preview" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                {combined.map(item => (
                  <PreviewCard
                    key={item.id}
                    item={item}
                    onClick={handleItemClick}
                    selectMode={selectMode}
                    selected={!!selected[item.id]}
                  />
                ))}
                {loadingMore && [...Array(4)].map((_, i) => <PreviewSkeletonCard key={`more-p-${i}`} />)}
              </div>
              {hasMore && <div ref={observerTarget} className="w-full h-[1px]"></div>}
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                {combined.map(item => (
                  <ListRow
                    key={item.id}
                    item={item}
                    onClick={handleItemClick}
                    selectMode={selectMode}
                    selected={!!selected[item.id]}
                  />
                ))}
                {loadingMore && [...Array(4)].map((_, i) => <ListSkeletonRow key={`more-l-${i}`} />)}
              </div>
              {hasMore && <div ref={observerTarget} className="w-full h-[1px]"></div>}
            </>
          )}
        </div>

        {/* Selection Footer */}
        {selectMode && (
          <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-[#111113]/80">
            <span className="text-[13px] font-bold text-gray-700 dark:text-gray-200">
              {selectedCount > 0 ? `${selectedCount}개 선택됨` : "항목을 선택하세요"}
            </span>
            <div className="flex items-center gap-2">
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected({})}
                  className="h-9 px-4 rounded-full text-[13px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  선택 해제
                </button>
              )}
              <button
                type="button"
                onClick={confirmSelection}
                disabled={selectedCount === 0}
                className="h-9 px-4 rounded-full text-[13px] font-bold bg-seexr-solid text-white hover:bg-seexr-solid/90 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              >
                추가하기
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
